import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import type { PayRateUnit, TeachingRole } from '@repo/shared-types';
import { TeacherAdhocExplanationPopover } from './ModeExplanationPopover';

export interface TeacherListRow {
  membershipId: string;
  role: TeachingRole;
  /** Slot mode only. undefined = the caller cannot see pay at all. */
  rateOverride?: number | null;
  /** Slot mode only. Unit for `rateOverride`, independent of the teacher's
   *  own rate unit — mirrors SlotTeacherRow. No control in this editor edits
   *  it (there is no unit picker), so it must be carried through untouched:
   *  dropping it on an unrelated save silently converts a migrated PER_HOUR
   *  override into PER_SESSION (see `emit()`). */
  rateOverrideUnit?: PayRateUnit | null;
  /** Session mode only, from the API's `SessionTeacherRow.fromSlot` — false
   *  earns this row the same "Đột xuất" badge a one-off student gets.
   *  Undefined (slot mode, or a row just added and not yet saved) renders no
   *  badge — only an explicit `false` does. */
  fromSlot?: boolean;
}

interface TeacherOption {
  id: string;
  name: string;
  color: string;
}

/**
 * One editor, two homes (SlotFormDialog and SessionDetailPage) — slots and
 * sessions differ only in whether a row can carry a pay override, which is
 * exactly what `showOverride` gates. Do not fork this into a second copy.
 *
 * Row 0 is always the đứng-tên teacher, resolved from `options` and pinned:
 * no remove control (swapping them is the GV chịu trách nhiệm select / đổi-GV
 * flow, not this list), role locked to Chính. Their `rateOverride` — an override
 * CARRIER row, not a second teacher (see `SlotTeacherRow`'s doc comment) — is
 * merged in from `rows` when present and never rendered as its own row.
 */
export function TeacherListEditor({
  ownerTeacherId,
  rows,
  onChange,
  options,
  disabled,
  showOverride,
  mode,
  ns,
}: {
  /** The đứng-tên teacher — rendered as the pinned first row. */
  ownerTeacherId: string;
  /** The OTHER teachers, plus (slot mode) the owner's override-carrier row. */
  rows: TeacherListRow[];
  onChange: (rows: TeacherListRow[]) => void;
  options: TeacherOption[];
  disabled?: boolean;
  /** Slot mode: reveal the collapsed "Tiền công riêng…" disclosure per row. */
  showOverride: boolean;
  /** 'slot': the owner row shows the đứng-tên hint (card/timetable-filter
   *  language — meaningless outside SlotFormDialog). 'session': suppressed —
   *  a per-buổi editor has neither a card nor a filter for the hint to describe. */
  mode: 'slot' | 'session';
  ns: 'scheduling';
}) {
  const { t } = useTranslation(ns);

  const ownerOption = options.find((o) => o.id === ownerTeacherId);
  const ownerRow = rows.find((r) => r.membershipId === ownerTeacherId);
  const otherRows = rows.filter((r) => r.membershipId !== ownerTeacherId);

  // Every membership already spoken for — owner plus every OTHER row. Used
  // both to compute what a fresh "Thêm giáo viên" row should default to, and
  // (per-row, with the row's own id added back) what a row's own Select may
  // still offer.
  const usedIds = new Set<string>([ownerTeacherId, ...otherRows.map((r) => r.membershipId)]);
  const unusedOptions = options.filter((o) => !usedIds.has(o.id));

  // onChange always emits the COMPLETE list, owner's carrier row included
  // when non-null / omitted when null — matching what replaceSlotTeachers
  // does server-side, so the form and the server agree about what got saved.
  const emit = (nextOtherRows: TeacherListRow[], nextOwnerOverride: number | null | undefined) => {
    const ownerOverride = nextOwnerOverride === undefined ? (ownerRow?.rateOverride ?? null) : nextOwnerOverride;
    // rateOverrideUnit has no UI control of its own — always carried forward
    // from the incoming ownerRow so an untouched (or value-only) save
    // round-trips a migrated unit byte-identical instead of nulling it.
    const carrier: TeacherListRow[] =
      ownerOverride != null
        ? [
            {
              membershipId: ownerTeacherId,
              role: 'CHINH',
              rateOverride: ownerOverride,
              rateOverrideUnit: ownerRow?.rateOverrideUnit ?? null,
            },
          ]
        : [];
    onChange([...carrier, ...nextOtherRows]);
  };

  const updateOtherRow = (index: number, patch: Partial<TeacherListRow>) => {
    emit(
      otherRows.map((r, i) => (i === index ? { ...r, ...patch } : r)),
      undefined,
    );
  };
  const removeOtherRow = (index: number) => {
    emit(
      otherRows.filter((_, i) => i !== index),
      undefined,
    );
  };
  // Default role TRO_GIANG: the overwhelmingly common second person in the
  // room is the trợ giảng, and a wrong default that is one click from right
  // beats a picker that is always two.
  const addTeacher = () => {
    const next = unusedOptions[0];
    if (!next) return;
    emit([...otherRows, { membershipId: next.id, role: 'TRO_GIANG', rateOverride: null, rateOverrideUnit: null }], undefined);
  };
  const setOwnerOverride = (value: number | null) => emit(otherRows, value);

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-surface-200 bg-surface-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ background: ownerOption?.color ?? '#94a3b8' }}
          />
          <span className="truncate text-sm font-medium text-surface-900">{ownerOption?.name ?? '—'}</span>
          <Badge variant="muted" className="shrink-0 text-[10px]">{t('slot.roleChinh')}</Badge>
        </div>
        {mode === 'slot' ? <p className="mt-1 text-xs text-surface-500">{t('slot.teacherOwnerHint')}</p> : null}
        {showOverride ? (
          <OverrideDisclosure value={ownerRow?.rateOverride ?? null} onChange={setOwnerOverride} disabled={disabled} />
        ) : null}
      </div>

      {otherRows.map((row, i) => {
        // Own id re-included so the Select can keep showing its own current
        // value — every OTHER row's id stays excluded.
        const rowOptions = options.filter((o) => o.id === row.membershipId || !usedIds.has(o.id));
        const name = options.find((o) => o.id === row.membershipId)?.name ?? '';
        return (
          <div key={row.membershipId} className="rounded-md border border-surface-200 px-3 py-2">
            <div className="flex items-center gap-2">
              <Select
                value={row.membershipId}
                onValueChange={(v) => updateOtherRow(i, { membershipId: v })}
                disabled={disabled}
              >
                <SelectTrigger className="h-9 min-w-0 flex-1 truncate">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rowOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
                        {opt.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === 'session' && row.fromSlot === false ? (
                <span className="shrink-0">
                  <TeacherAdhocExplanationPopover teacherName={name} />
                </span>
              ) : null}
              {/* Two values do not need a dropdown. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                className="shrink-0"
                onClick={() => updateOtherRow(i, { role: row.role === 'CHINH' ? 'TRO_GIANG' : 'CHINH' })}
              >
                {row.role === 'CHINH' ? t('slot.roleChinh') : t('slot.roleTroGiang')}
              </Button>
              <button
                type="button"
                aria-label={t('slot.removeTeacher', { name })}
                onClick={() => removeOtherRow(i)}
                disabled={disabled}
                className="flex min-h-[44px] w-8 shrink-0 items-center justify-center text-surface-400 transition-colors hover:text-destructive sm:min-h-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {showOverride ? (
              <OverrideDisclosure
                value={row.rateOverride ?? null}
                onChange={(v) => updateOtherRow(i, { rateOverride: v })}
                disabled={disabled}
              />
            ) : null}
          </div>
        );
      })}

      {unusedOptions.length > 0 ? (
        <Button type="button" variant="outline" size="sm" disabled={disabled} className="gap-1.5" onClick={addTeacher}>
          <Plus className="h-4 w-4" />
          {t('slot.addTeacher')}
        </Button>
      ) : null}
    </div>
  );
}

// Collapsed by default; starts OPEN when the row already has a non-null
// rateOverride — a rate that is already set must not be hidden behind a
// click. `open` is local state seeded once from the incoming value, not
// re-derived every render, so clearing the input to retype doesn't collapse
// it back out from under the teacher mid-edit.
function OverrideDisclosure({
  value,
  onChange,
  disabled,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  const { t: tp } = useTranslation('payroll');
  const [open, setOpen] = useState(value != null);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="mt-1.5 flex min-h-[44px] items-center text-xs font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700 sm:min-h-0"
      >
        {tp('settings.slotOverride')}
      </button>
    );
  }

  return (
    <div className="mt-1.5 max-w-[160px]">
      <Input
        type="number"
        min="0"
        value={value != null ? String(value) : ''}
        onChange={(e) => onChange(e.target.value.trim() ? Number(e.target.value) : null)}
        disabled={disabled}
        placeholder="—"
      />
      <p className="mt-1 text-xs text-surface-500">{tp('settings.slotOverrideHelper')}</p>
    </div>
  );
}
